import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SubjectArea } from '@tran-monorepo/interfaces/applications';
import { merge, startWith, tap } from 'rxjs';
import { subjectAreasAtLeastOneRequired } from '../../../utils';
import { CustomValueSelectFieldComponent } from '../custom-value-select/custom-value-select-field.component';
import { CustomValueSelectItem } from '../custom-value-select/custom-value-select-item.type';

@Component({
    selector: 'tran-subject-area-selector',
    imports: [ReactiveFormsModule, TranslateModule, CustomValueSelectFieldComponent],
    templateUrl: './subject-area-selector.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubjectAreaSelectorComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);

    public readonly predefinedControl = input.required<FormControl<SubjectArea[]>>();
    public readonly customControl = input.required<FormArray<FormControl<string>>>();
    public readonly subjectAreas = input<SubjectArea[]>([]);
    public readonly title = input<string>('');
    public readonly label = input<string>('');
    public readonly readonly = input(false);
    public readonly showRequiredMarker = input(true);

    protected readonly internalGroup = new FormGroup({
        selection: new FormControl<CustomValueSelectItem<SubjectArea>[]>([], { nonNullable: true })
    });

    protected get selectionControl() {
        return this.internalGroup.controls.selection;
    }

    protected readonly isRequired = computed(() => {
        return !!this.predefinedControl().parent?.hasValidator(subjectAreasAtLeastOneRequired);
    });

    ngOnInit(): void {
        this.setupValidation();

        merge(this.buildExternalToInternalSync(), this.buildInternalToExternalSync(), this.buildTouchedStateSync())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
    }

    private setupValidation(): void {
        if (!this.isRequired()) return;
        this.selectionControl.addValidators([Validators.required, Validators.minLength(1)]);
        this.selectionControl.updateValueAndValidity({ emitEvent: false });
    }

    private buildExternalToInternalSync() {
        return merge(this.predefinedControl().valueChanges, this.customControl().valueChanges).pipe(
            startWith(null),
            tap(() => this.syncSelectionFromControls())
        );
    }

    private buildInternalToExternalSync() {
        return this.selectionControl.valueChanges.pipe(tap(selection => this.applySelection(selection)));
    }

    private buildTouchedStateSync() {
        return merge(this.predefinedControl().events, this.customControl().events).pipe(
            tap(() => this.syncTouchedState())
        );
    }

    private syncTouchedState(): void {
        if (this.predefinedControl().touched || this.customControl().touched) {
            this.selectionControl.markAsTouched();
        }
    }

    private syncSelectionFromControls(): void {
        const items: CustomValueSelectItem<SubjectArea>[] = [
            ...this.predefinedControl().value.map(value => ({ type: 'predefined', value }) as const),
            ...this.customControl().value.map(value => ({ type: 'custom', value }) as const)
        ];

        this.selectionControl.setValue(items, { emitEvent: false });
    }

    private applySelection(selection: CustomValueSelectItem<SubjectArea>[]): void {
        const predefined = selection.filter(item => item.type === 'predefined').map(item => item.value);
        const custom = selection.filter(item => item.type === 'custom').map(item => item.value);

        this.predefinedControl().setValue(predefined, { emitEvent: false });

        this.customControl().clear({ emitEvent: false });
        custom.forEach(value =>
            this.customControl().push(new FormControl(value, { nonNullable: true }), { emitEvent: false })
        );

        [this.predefinedControl(), this.customControl()].forEach(control => {
            control.markAsTouched();
            control.markAsDirty();
        });

        this.customControl().updateValueAndValidity();
    }
}
